import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { getDvSourceImageUrl } from "../../functions/themeAssets";
import { getMatchPairs, shuffleMatchAnswers } from "./stageContent";
import { StageLoader, StageShell, useStageRun } from "./stageShared";
import { nyAlert } from "../nyPopups";

/** Stage 4 — match pairs (Admin → Game Content → Match Pair). Last stage of the run. */
const STAGE_NUM = 4;
const DEFAULT_HEADING = "Match-up game";
// Distinct pair colors that keep white badge numbers readable (green/red are reserved for results).
const PAIR_COLORS = ["#f97316", "#0284c7", "#7c3aed", "#db2777", "#0d9488", "#ca8a04"];
const CONFETTI_COLORS = ["#ffc83d", "#ff5fa2", "#a66bff", "#4cc9ff", "#ffffff"];

/** Falling confetti behind the "Correct Answer is" card. */
function Confetti() {
  const pieces = useMemo(
    () =>
      Array.from({ length: 36 }, (_, i) => ({
        left: Math.random() * 100,
        delay: Math.random() * 2.5,
        duration: 3.5 + Math.random() * 3,
        rotate: Math.random() * 360,
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
        wide: i % 3 === 0,
      })),
    []
  );
  return (
    <div className="ny-confetti" aria-hidden>
      {pieces.map((p, i) => (
        <span
          key={i}
          className={p.wide ? "is-ribbon" : ""}
          style={{
            left: `${p.left}%`,
            background: p.color,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
            "--r": `${p.rotate}deg`,
          }}
        />
      ))}
    </div>
  );
}

/** Arrowhead length / half-width, and the gap kept between the tip and the name tile. */
const ARROW_LEN = 12;
const ARROW_HALF = 7;
const ARROW_GAP = 4;

/** An element's box relative to `root`, via offsetLeft/Top (ignores hover transforms). */
function boxIn(el, root) {
  let left = 0;
  let top = 0;
  for (let node = el; node && node !== root; node = node.offsetParent) {
    left += node.offsetLeft;
    top += node.offsetTop;
  }
  return { left, top, width: el.offsetWidth, height: el.offsetHeight };
}

/**
 * Curved, self-drawing arrows from each picture to its chosen name — the React take on
 * old/stage4.php's LeaderLine `show("draw")`. Positions come from offsetLeft/Top, which
 * ignore the tiles' hover lift, so arrows don't jump while the pointer moves.
 */
function MatchArrows({ boardRef, imageRefs, nameRefs, links, linkMeta, drawnRef }) {
  const [arrows, setArrows] = useState([]);

  const measure = useCallback(() => {
    const board = boardRef.current;
    if (!board) return;
    const next = [];
    Object.entries(links).forEach(([imageId, answerId]) => {
      const fromEl = imageRefs.current[imageId];
      const toEl = nameRefs.current[answerId];
      const meta = linkMeta[imageId];
      if (!fromEl || !toEl || !meta) return;
      const from = boxIn(fromEl, board);
      const to = boxIn(toEl, board);
      const x1 = from.left + from.width;
      const y1 = from.top + from.height / 2;
      const tipX = to.left - ARROW_GAP;
      const y2 = to.top + to.height / 2;
      const x2 = tipX - ARROW_LEN + 1; // line stops inside the arrowhead
      const bend = Math.max(30, (x2 - x1) * 0.5);
      next.push({
        key: `${imageId}-${answerId}`,
        color: meta.color,
        d: `M ${x1} ${y1} C ${x1 + bend} ${y1}, ${x2 - bend} ${y2}, ${x2} ${y2}`,
        head: `${tipX},${y2} ${tipX - ARROW_LEN},${y2 - ARROW_HALF} ${tipX - ARROW_LEN},${y2 + ARROW_HALF}`,
        start: [x1, y1],
      });
    });
    setArrows(next);
    // Forget only pairs that were really unlinked (not ones skipped by a measure), so
    // matching them again draws afresh while existing arrows never replay.
    const keys = new Set(Object.entries(links).map(([imageId, answerId]) => `${imageId}-${answerId}`));
    drawnRef.current.forEach((k) => {
      if (!keys.has(k)) drawnRef.current.delete(k);
    });
  }, [boardRef, imageRefs, nameRefs, links, linkMeta, drawnRef]);

  useLayoutEffect(() => {
    measure();
  }, [measure]);

  // Re-measure when the board resizes or a picture finishes loading (changes its size).
  useEffect(() => {
    const board = boardRef.current;
    if (!board || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(board);
    Object.values(imageRefs.current).forEach((el) => el && ro.observe(el));
    return () => ro.disconnect();
  }, [boardRef, imageRefs, measure]);

  return (
    <svg className="mp-arrows" aria-hidden>
      {arrows.map((a) => (
        <MatchArrow key={a.key} arrow={a} drawnRef={drawnRef} />
      ))}
    </svg>
  );
}

/**
 * One arrow. It's drawn by a one-off Web Animation the first time its pair appears;
 * after that it's plain static SVG, so re-renders, re-measures or React moving the
 * node never replay it — only the newest match animates.
 */
function MatchArrow({ arrow, drawnRef }) {
  const dotRef = useRef(null);
  const lineRef = useRef(null);
  const headRef = useRef(null);

  useLayoutEffect(() => {
    const drawn = drawnRef.current;
    if (drawn.has(arrow.key)) return;
    drawn.add(arrow.key);
    const line = lineRef.current;
    if (!line?.animate || window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    dotRef.current.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 220, easing: "ease-out" });
    line.animate([{ strokeDashoffset: 1 }, { strokeDashoffset: 0 }], { duration: 650, easing: "ease-out" });
    headRef.current.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration: 220,
      delay: 560,
      easing: "ease-out",
      fill: "backwards",
    });
  }, [arrow.key, drawnRef]);

  return (
    <g className="mp-arrow" style={{ "--mp-link": arrow.color }}>
      <circle ref={dotRef} className="mp-arrow-dot" cx={arrow.start[0]} cy={arrow.start[1]} r="5" />
      <path ref={lineRef} className="mp-arrow-line" d={arrow.d} pathLength="1" />
      <polygon ref={headRef} className="mp-arrow-head" points={arrow.head} />
    </g>
  );
}

export default function Stage4MatchPairs() {
  const run = useStageRun(STAGE_NUM);
  const { themeData, isLoading, pointsPerCorrect, addPoints, finishStage, startTimer } = run;

  const heading = String(themeData?.match_pair_title || "").trim() || DEFAULT_HEADING;
  const subheading = String(themeData?.match_pair_subtitle || "").trim();
  const pairs = useMemo(() => getMatchPairs(themeData), [themeData]);
  const answerOrder = useMemo(() => shuffleMatchAnswers(pairs), [pairs]);

  // links: image pair id → answer pair id
  const [links, setLinks] = useState({});
  const [pickedImage, setPickedImage] = useState(null);
  const [summary, setSummary] = useState(null); // set after Next → "Correct Answer is" screen
  const [busy, setBusy] = useState(false);
  const answersRef = useRef([]);
  const finished = useRef(false);
  const boardRef = useRef(null);
  const imageRefs = useRef({});
  const nameRefs = useRef({});
  const drawnRef = useRef(new Set()); // arrow keys already animated (see MatchArrow)

  useEffect(() => {
    if (isLoading || finished.current) return;
    if (!pairs.length) {
      finished.current = true;
      finishStage();
      return;
    }
    startTimer();
  }, [isLoading, pairs.length, finishStage, startTimer]);

  // Badge number + color per link, in the order the images appear.
  // Tied to the picture's position, so a pair keeps its number / color whatever order
  // the player matches in.
  const linkMeta = useMemo(() => {
    const meta = {};
    pairs.forEach((p, n) => {
      if (links[p.id] == null) return;
      meta[p.id] = { number: n + 1, color: PAIR_COLORS[n % PAIR_COLORS.length] };
    });
    return meta;
  }, [links, pairs]);

  const imageForAnswer = useMemo(() => {
    const map = {};
    Object.entries(links).forEach(([imageId, answerId]) => {
      map[answerId] = Number(imageId);
    });
    return map;
  }, [links]);

  const pickImage = (id) => {
    if (summary) return;
    setPickedImage((prev) => (prev === id ? null : id));
  };

  const pickAnswer = (answerId) => {
    if (summary) return;
    if (pickedImage == null) {
      // Tapping an already-matched name without a picked image unlinks it.
      const linkedImage = imageForAnswer[answerId];
      if (linkedImage != null) {
        setLinks((prev) => {
          const next = { ...prev };
          delete next[linkedImage];
          return next;
        });
      }
      return;
    }
    setLinks((prev) => {
      const next = { ...prev };
      // A name can only belong to one image.
      Object.keys(next).forEach((imageId) => {
        if (next[imageId] === answerId) delete next[imageId];
      });
      next[pickedImage] = answerId;
      return next;
    });
    setPickedImage(null);
  };

  const handleReset = () => {
    if (summary || busy) return;
    setLinks({});
    setPickedImage(null);
  };

  const allLinked = pairs.length > 0 && Object.keys(links).length === pairs.length;

  const handleSubmit = () => {
    if (busy || summary || finished.current) return;
    if (!allLinked) {
      nyAlert({
        kind: "info",
        title: "Match every picture",
        text: "Pick a picture on the left, then its name on the right, until every picture has a match.",
        confirmText: "Got it",
      });
      return;
    }

    let correctCount = 0;
    answersRef.current = pairs.map((p, i) => {
      const chosen = pairs.find((x) => x.id === links[p.id]);
      const isCorrect = links[p.id] === p.id;
      if (isCorrect) correctCount += 1;
      return {
        questionId: `pair-${p.id}`,
        questionText: p.image,
        userAnswer: chosen?.answer || "",
        correctAnswer: p.answer,
        isCorrect,
        questionNumber: i + 1,
      };
    });

    const points = correctCount * pointsPerCorrect;
    addPoints(points);
    setSummary({ correct: correctCount, total: pairs.length, points });
  };

  const handleContinue = () => {
    if (busy || finished.current) return;
    setBusy(true);
    finished.current = true;
    finishStage({ answers: answersRef.current });
  };

  if (isLoading || !pairs.length) return <StageLoader />;

  // "Correct Answer is" — every picture with its right name (reference screen 6).
  if (summary) {
    return (
      <StageShell run={run} heading={heading} subheading="" className="mp-reveal-page">
        <Confetti />
        <main className="ny-content mp-reveal ny-cream-card" style={{ "--mp-count": pairs.length }}>
          <h2 className="mp-reveal-title">Correct Answer is</h2>
          <ol className="mp-reveal-list">
            {pairs.map((p, i) => {
              const right = links[p.id] === p.id;
              return (
                <li key={p.id} className={`mp-reveal-row${right ? " is-right" : " is-missed"}`}>
                  <span className="mp-reveal-num">{i + 1}.</span>
                  <img src={getDvSourceImageUrl(p.image)} alt="" className="mp-reveal-img" />
                  <span className="mp-reveal-name">{p.answer}</span>
                  <i
                    className={`mp-reveal-mark fa-solid ${right ? "fa-circle-check" : "fa-circle-xmark"}`}
                    aria-label={right ? "You matched this" : "You missed this"}
                  />
                </li>
              );
            })}
          </ol>
          <p className="mp-reveal-score">
            You matched <strong>{summary.correct}</strong> of {summary.total} · +{summary.points}{" "}
            points
          </p>
          <button
            type="button"
            className="ny-btn ny-btn--primary"
            onClick={handleContinue}
            disabled={busy}
          >
            {busy ? "Saving…" : "Continue"}
            <i className="fa-solid fa-arrow-right" aria-hidden />
          </button>
        </main>
      </StageShell>
    );
  }

  return (
    <StageShell run={run} heading={heading} subheading={subheading}>
      <main className="ny-content mp-stage">
        <div className="mp-board" ref={boardRef} style={{ "--mp-count": pairs.length }}>
          <ul className="mp-column mp-column--images" aria-label="Pictures">
            {pairs.map((p) => {
              const meta = linkMeta[p.id];
              const src = getDvSourceImageUrl(p.image);
              let state = "";
              if (pickedImage === p.id) state = " is-picked";
              else if (meta) state = " is-linked";
              return (
                <li key={p.id}>
                  <button
                    type="button"
                    className={`mp-image${state}`}
                    style={meta ? { "--mp-link": meta.color } : undefined}
                    onClick={() => pickImage(p.id)}
                    aria-pressed={pickedImage === p.id}
                    aria-label={meta ? `Picture matched as pair ${meta.number}` : "Picture"}
                  >
                    {/* The frame hugs the picture itself — arrows start from its edge. */}
                    <span
                      className="mp-image-frame"
                      ref={(el) => {
                        // Never store the null React passes between renders — a measure in
                        // that gap would drop (and later re-draw) every arrow.
                        if (el) imageRefs.current[p.id] = el;
                      }}
                    >
                      <img src={src} alt="" draggable={false} />
                      {meta ? <span className="mp-badge">{meta.number}</span> : null}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>

          <ul className="mp-column mp-column--names" aria-label="Names">
            {answerOrder.map((answerId) => {
              const pair = pairs.find((p) => p.id === answerId);
              const linkedImage = imageForAnswer[answerId];
              const meta = linkedImage != null ? linkMeta[linkedImage] : null;
              return (
                <li key={answerId}>
                  <button
                    ref={(el) => {
                      if (el) nameRefs.current[answerId] = el;
                    }}
                    type="button"
                    className={`mp-item mp-name${meta ? " is-linked" : ""}${
                      pickedImage != null ? " is-ready" : ""
                    }`}
                    style={meta ? { "--mp-link": meta.color } : undefined}
                    onClick={() => pickAnswer(answerId)}
                  >
                    {meta ? <span className="mp-badge">{meta.number}</span> : null}
                    <span className="mp-name-label">{pair?.answer}</span>
                  </button>
                </li>
              );
            })}
          </ul>

          {/* After the columns, so their tile refs are attached before the arrows measure. */}
          <MatchArrows
            boardRef={boardRef}
            imageRefs={imageRefs}
            nameRefs={nameRefs}
            links={links}
            linkMeta={linkMeta}
            drawnRef={drawnRef}
          />
        </div>

        <div className="mp-actions">
          <button type="button" className="ny-btn ny-btn--primary" onClick={handleSubmit}>
            Next
            <i className="fa-solid fa-arrow-right" aria-hidden />
          </button>
          <button
            type="button"
            className="ny-btn ny-btn--light"
            onClick={handleReset}
            disabled={!Object.keys(links).length}
          >
            <i className="fa-solid fa-rotate-left" aria-hidden />
            Reset
          </button>
        </div>
      </main>
    </StageShell>
  );
}
